import type { FieldErrorProp } from './props';
import { computed, type MaybeRefOrGetter, toRef } from 'vue';

export interface UseFieldErrorsOptions {
    maxErrorsInMessage?: number;
}

/**
 * Interprets the `errors` properties on a Form Field.
 *
 * @param errorsProp
 * @param opts
 */
export function useFieldErrors(
    errorsProp: MaybeRefOrGetter<FieldErrorProp>,
    opts?: MaybeRefOrGetter<UseFieldErrorsOptions>
) {
    const errorInput = toRef(errorsProp);
    const options = toRef(opts);

    const errors = computed(() => {
        if (
            typeof errorInput.value === 'string' &&
            errorInput.value.trim().length > 0
        ) {
            return [errorInput.value];
        } else if (
            Array.isArray(errorInput.value) &&
            errorInput.value.length > 0
        ) {
            return errorInput.value;
        } else {
            return [];
        }
    });

    const hasErrors = computed<boolean>(
        () => errorInput.value === true || errors.value.length > 0
    );

    const errorMessages = computed<string[]>(() =>
        errors.value.map((e) => (typeof e === 'string' ? e : e.$message))
    );

    const errorMessage = computed<string | null>(() => {
        if (!hasErrors.value) return null;
        if (errorMessages.value.length <= 0) return null;

        const maxErrorsInMessage = options.value?.maxErrorsInMessage ?? 3;
        let message = errorMessages.value
            .slice(0, maxErrorsInMessage)
            .join(', ');
        const extraErrorCount = errorMessages.value.length - maxErrorsInMessage;
        if (extraErrorCount > 0) {
            message += ` + ${extraErrorCount} andere fouten.`;
        }

        return message;
    });

    return { errorInput, errors, hasErrors, errorMessage };
}
